import React, { useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import { EntityType, Organisation, OrganisationType, TradingEntity } from 'api'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { Tabs } from 'view/components/tabs'
import { DialogField, DialogRow } from 'view/components/inputs'
import { TradingEntityDetailsPanel } from './TradingEntityDetailsPanel'
import { ProductsPanel } from './ProductsPanel'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { Locale, Translation } from 'locale'
import { createTradingEntityByIdGetterSelector } from 'selectors/tradingEntitySelectors'
import { noop } from 'lodash'
import { ViewTradingEntityRelationshipsPanel } from './TradingEntityRelationshipsPanel/ViewTradingEntityRelationshipsPanel'
import { ViewTradingEntityAliasesPanel } from './TradingEntityAliasesPanel'
import { ViewTradingEntityDirectClearingPanel } from './TradingEntityClearingPanel/ViewTradingEntityDirectClearingPanel'
import { ViewTradingEntitySponsoredClearingPanel } from './TradingEntityClearingPanel/ViewTradingEntitySponsoredClearingPanel'

export interface ViewTradingEntityDialogProps {
  //Own Props
  tradingEntityEntityId: string
  onClose?: () => unknown

  //State Props
  getTradingEntity: (tradingEntityId: string) => TradingEntity | null
  getOrganisation: (organisationId: string) => Organisation | null
}

const mapStateToProps = createStructuredSelector({
  getTradingEntity: createTradingEntityByIdGetterSelector,
  getOrganisation: createEntityGetterSelector(EntityType.ORGANISATION)
})

const Content = styled(Groupbox)`
  padding: 20px 20px 0 20px;
  min-width: 940px;
  min-height: 450px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
`

function getTabs(organisation: Organisation | null): Array<Translation> {
  const tabs = [Translation.TC_TRADING_ENTITY_DETAILS, Translation.TC_PRODUCTS]

  const organisationTypes = organisation?.organisationTypes

  if (organisationTypes?.includes(OrganisationType.SELL_SIDE)) {
    tabs.push(Translation.TC_INITIATOR_RELATIONSHIPS)
  }
  if (organisationTypes?.includes(OrganisationType.BUY_SIDE)) {
    tabs.push(Translation.TC_RESPONDER_RELATIONSHIPS)
  }

  tabs.push(Translation.TC_ALIASES)
  tabs.push(Translation.TC_DIRECT_CLEARING)
  tabs.push(Translation.TC_SPONSORED_CLEARING)

  return tabs
}

export const ViewTradingEntityDialogComponent: React.FunctionComponent<ViewTradingEntityDialogProps> = props => {
  const { tradingEntityEntityId, onClose, getTradingEntity, getOrganisation } = props

  const [selectedTab, setSelectedTab] = useState<Translation>(Translation.TC_TRADING_ENTITY_DETAILS)
  const tradingEntity = getTradingEntity(tradingEntityEntityId)

  if (!tradingEntity) {
    return null
  }

  const title = tradingEntity.publishedName
  const organisation = getOrganisation(tradingEntity.organisationId)

  const getSelectedTabContent = () => {
    const tradingEntityDetailsPanel = (
      <TradingEntityDetailsPanel
        tradingEntity={tradingEntity}
        isNewTradingEntity={false}
        getOrganisation={getOrganisation}
        readOnly={true}
        onUpdate={noop}
        validationIssues={[]}
      />
    )
    const productsPanel = <ProductsPanel tradingEntity={tradingEntity} readOnly={true} onUpdate={noop} />
    const responderRelationshipPanel = (
      <ViewTradingEntityRelationshipsPanel tradingEntity={tradingEntity} organisationType={OrganisationType.BUY_SIDE} />
    )
    const initiatorRelationshipPanel = (
      <ViewTradingEntityRelationshipsPanel tradingEntity={tradingEntity} organisationType={OrganisationType.SELL_SIDE} />
    )
    const aliasesPanel = <ViewTradingEntityAliasesPanel tradingEntityId={tradingEntity.id} />
    const directClearingPanel = <ViewTradingEntityDirectClearingPanel tradingEntityId={tradingEntity.id} />

    const sponsoredClearingPanel = <ViewTradingEntitySponsoredClearingPanel tradingEntityId={tradingEntity.id} />

    switch (selectedTab) {
      case Translation.TC_TRADING_ENTITY_DETAILS:
        return tradingEntityDetailsPanel
      case Translation.TC_PRODUCTS:
        return productsPanel
      case Translation.TC_INITIATOR_RELATIONSHIPS:
        return initiatorRelationshipPanel
      case Translation.TC_RESPONDER_RELATIONSHIPS:
        return responderRelationshipPanel
      case Translation.TC_ALIASES:
        return aliasesPanel
      case Translation.TC_DIRECT_CLEARING:
        return directClearingPanel
      case Translation.TC_SPONSORED_CLEARING:
        return sponsoredClearingPanel
      default:
        return null
    }
  }

  const onChangeTab = (tab: Translation) => {
    setSelectedTab(tab)
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  return (
    <ModalWindow title={title}>
      <Tabs dataTestId={'TradingEntityDialog-Tabs'} selectedTab={selectedTab} onChangeTab={onChangeTab} tabs={getTabs(organisation)} />
      <Content>{getSelectedTabContent()}</Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'TradingEntityDialog-TextButton-ButtonCancel'} type={TextButtonType.Tertiary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'OrganisationDialog-TextButton-ButtonSave'}
              type={TextButtonType.Primary}
              disabled={true}
              onClick={noop}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const ViewTradingEntityDialog = connect(mapStateToProps)(ViewTradingEntityDialogComponent)
